import { MenuItem, TextField } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';

/**
 * Static catalog used only when no LOB is selected yet or the selected
 * LOB declares no `supportedLanguages`.
 */
export const FALLBACK_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt', 'it', 'ja', 'zh'];

interface PromptLanguageSelectProps {
  /** LOB the prompt belongs to — drives the available language options. */
  lineOfBizId: number | null | undefined;
  value: string | null | undefined;
  onChange: (language: string) => void;
}

/**
 * Language dropdown for the New/Edit Prompt form. Scopes its options to the
 * `supportedLanguages` of the LOB currently selected in the form (not the
 * sidebar's active LOB) so the choices always match where the prompt will
 * actually live. Falls back to the static catalog when the LOB has none.
 *
 * If the current value falls outside the resolved options (e.g. the user
 * switched to an LOB that doesn't support it), it snaps the selection to the
 * first available language so the form can't submit an unsupported value.
 */
export function PromptLanguageSelect({ lineOfBizId, value, onChange }: PromptLanguageSelectProps) {
  const lob = useQuery({
    queryKey: ['line-of-business', lineOfBizId],
    queryFn: () => resources.linesOfBusiness.get(lineOfBizId!),
    enabled: lineOfBizId != null,
  });

  const languageOptions = useMemo(() => {
    const lobLangs = lob.data?.supportedLanguages ?? [];
    return lobLangs.length > 0 ? lobLangs : FALLBACK_LANGUAGES;
  }, [lob.data?.supportedLanguages]);

  // Keep the selection inside the available set. Only correct once the LOB
  // has resolved (its query isn't fetching) to avoid snapping away from a
  // valid value while the supported list is still loading.
  useEffect(() => {
    if (lob.isFetching) return;
    if (value && languageOptions.includes(value)) return;
    const next = languageOptions[0];
    if (next && next !== value) onChange(next);
  }, [lob.isFetching, languageOptions, value, onChange]);

  return (
    <TextField
      select
      required
      fullWidth
      label="Language"
      value={languageOptions.includes(value ?? '') ? value : ''}
      onChange={(e) => onChange(e.target.value)}
    >
      {languageOptions.map((l) => (
        <MenuItem key={l} value={l}>
          {l}
        </MenuItem>
      ))}
    </TextField>
  );
}
